"use client";
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { visitorService, canManageMembers, canAddVisitors, type Visitor } from '@/lib/supabase-service';

const HOW_HEARD_OPTIONS = ['Friend / Family', 'Social Media', 'Website', 'Drive By', 'Community Event', 'Other'];
const INTEREST_OPTIONS = ['Membership', 'Small Groups', 'Volunteering', 'Youth Ministry', 'Prayer Team', 'Bible Study'];

export default function VisitorIntakePage() {
  const router = useRouter();
  const { user, profile, loading: authLoading } = useAuth();
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', howHeard: '', firstTime: true, prayerRequest: '', interestedIn: [] as string[] });

  useEffect(() => {
    if (!authLoading && !user) {
      router.push('/login');
      return;
    }
    if (!authLoading && user) {
      loadVisitors();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  useEffect(() => {
    if (!user) return;
    const unsubscribe = visitorService.subscribeToChanges((updatedVisitors) => {
      setVisitors(updatedVisitors);
    });
    return unsubscribe;
  }, [user]);

  async function loadVisitors() {
    setDataLoading(true);
    try {
      const data = await visitorService.getAll();
      setVisitors(data);
    } finally {
      setDataLoading(false);
    }
  }

  const canEdit = profile ? canManageMembers(profile.role) : false;
  const canAdd = profile ? canAddVisitors(profile.role) : false;

  function toggleInterest(item: string) {
    setForm(f => ({ ...f, interestedIn: f.interestedIn.includes(item) ? f.interestedIn.filter(i => i !== item) : [...f.interestedIn, item] }));
  }

  async function handleSubmit() {
    if (!form.name.trim()) return;
    const today = new Date().toISOString().split('T')[0];
    await visitorService.create({ ...form, date: today, followedUp: false });
    const updated = await visitorService.getAll();
    setVisitors(updated);
    setForm({ name: '', phone: '', email: '', address: '', howHeard: '', firstTime: true, prayerRequest: '', interestedIn: [] });
    setShowForm(false);
  }

  async function toggleFollowUp(id: string) {
    const visitor = visitors.find(v => v.id === id);
    if (!visitor) return;
    await visitorService.update(id, { followedUp: !visitor.followedUp });
    setVisitors(prev => prev.map(v => v.id === id ? { ...v, followedUp: !v.followedUp } : v));
  }

  async function handleDelete(id: string) {
    await visitorService.delete(id);
    setVisitors(prev => prev.filter(v => v.id !== id));
  }

  if (authLoading) return (
    <div className="min-h-screen bg-sanctuary flex items-center justify-center">
      <div className="noise-overlay" aria-hidden="true" />
      <p className="font-mono text-sm text-silver-dim relative z-10">Loading…</p>
    </div>
  );

  if (!user) return null;

  return (
    <div className="min-h-screen bg-sanctuary">
      <div className="noise-overlay" aria-hidden="true" />
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
          <div>
            <span className="section-label block mb-2">Flock // Visitor Intake</span>
            <h1 className="text-silver font-black text-3xl tracking-tight uppercase">Visitors</h1>
            <p className="text-silver-dim text-sm mt-1 font-light">Log first-time and returning visitors and track follow-up.</p>
          </div>
          {canAdd && (
            <button onClick={() => setShowForm(true)} className="btn-gold text-[11px] px-6 py-3 font-mono tracking-widest inline-flex items-center gap-2 self-start sm:self-auto">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
              Log Visitor
            </button>
          )}
        </div>

        <div className="grid grid-cols-3 gap-px bg-border-strong border border-border-strong mb-8">
          {[
            { label: 'Total Visitors', value: visitors.length },
            { label: 'Awaiting Follow-Up', value: visitors.filter(v => !v.followedUp).length },
            { label: 'Followed Up', value: visitors.filter(v => v.followedUp).length },
          ].map(stat => (
            <div key={stat.label} className="bento-cell px-6 py-5">
              <div className="stat-number text-3xl">{stat.value}</div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-silver-dim opacity-60 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {dataLoading && (
          <div className="border border-border-strong py-16 text-center">
            <p className="font-mono text-sm text-silver-dim opacity-50">Loading visitors…</p>
          </div>
        )}
        {!dataLoading && visitors.length === 0 && (
          <div className="border border-border-strong py-16 text-center">
            <p className="font-mono text-sm text-silver-dim opacity-50">No visitors logged yet.</p>
          </div>
        )}
        {!dataLoading && (
          <div className="space-y-px border border-border-strong">
            {visitors.map(v => (
              <div key={v.id} className={`bento-cell px-6 py-5 flex flex-col sm:flex-row sm:items-start gap-4 ${v.followedUp ? 'opacity-60' : ''}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3 mb-1">
                    <span className="text-silver font-semibold text-sm">{v.name}</span>
                    {v.firstTime && <span className="font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 border border-gold/40 text-gold bg-gold/10">First Time</span>}
                    <span className={`font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 border ${v.followedUp ? 'border-green-400/40 text-green-400 bg-green-400/10' : 'border-silver-dim/30 text-silver-dim'}`}>{v.followedUp ? 'Followed Up' : 'Pending'}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mb-2">
                    <span className="font-mono text-xs text-silver-dim">{v.phone}</span>
                    {v.email && <span className="font-mono text-xs text-silver-dim opacity-70">{v.email}</span>}
                    <span className="font-mono text-xs text-silver-dim opacity-50">Visited: {v.date}</span>
                    {v.howHeard && <span className="font-mono text-xs text-silver-dim opacity-50">Via: {v.howHeard}</span>}
                  </div>
                  {v.interestedIn.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {v.interestedIn.map(i => <span key={i} className="font-mono text-[10px] tracking-wider uppercase px-2 py-0.5 border border-border-strong text-silver-dim">{i}</span>)}
                    </div>
                  )}
                  {v.prayerRequest && <p className="text-silver-dim text-xs italic opacity-70">&ldquo;{v.prayerRequest}&rdquo;</p>}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {canEdit && (
                    <>
                      <button onClick={() => toggleFollowUp(v.id)} className={`font-mono text-[10px] tracking-wider uppercase px-4 py-2 border transition-colors ${v.followedUp ? 'border-border-strong text-silver-dim hover:border-gold hover:text-gold' : 'border-green-400/40 text-green-400 hover:bg-green-400/10'}`}>
                        {v.followedUp ? 'Undo' : 'Mark Followed Up'}
                      </button>
                      <button onClick={() => handleDelete(v.id)} className="font-mono text-[10px] tracking-wider text-silver-dim hover:text-red-400 transition-colors uppercase">Remove</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-sanctuary/80 backdrop-blur-sm px-4 py-8 overflow-y-auto">
          <div className="bg-vestment border border-border-strong w-full max-w-lg p-8 my-auto">
            <h2 className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mb-6">New Visitor Intake</h2>
            <div className="space-y-4">
              {[
                { label: 'Full Name *', key: 'name', type: 'text', placeholder: 'Visitor full name' },
                { label: 'Phone', key: 'phone', type: 'tel', placeholder: '(555) 000-0000' },
                { label: 'Email', key: 'email', type: 'email', placeholder: 'visitor@email.com' },
                { label: 'Address', key: 'address', type: 'text', placeholder: '123 Main St, City, State' },
              ].map(({ label, key, type, placeholder }) => (
                <div key={key}>
                  <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">{label}</label>
                  <input type={type} placeholder={placeholder} value={(form as Record<string, string>)[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2.5 placeholder:text-silver-dim/40 focus:outline-none focus:border-gold transition-colors" />
                </div>
              ))}
              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">How Did You Hear About Us?</label>
                <select value={form.howHeard} onChange={e => setForm(f => ({ ...f, howHeard: e.target.value }))} className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2.5 focus:outline-none focus:border-gold transition-colors">
                  <option value="">Select…</option>
                  {HOW_HEARD_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-2">Interested In</label>
                <div className="flex flex-wrap gap-2">
                  {INTEREST_OPTIONS.map(item => (
                    <button key={item} type="button" onClick={() => toggleInterest(item)} className={`font-mono text-[10px] tracking-wider uppercase px-3 py-1.5 border transition-colors ${form.interestedIn.includes(item) ? 'bg-gold text-sanctuary border-gold' : 'bg-transparent text-silver-dim border-border-strong hover:border-gold hover:text-gold'}`}>{item}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">Prayer Request</label>
                <textarea placeholder="Optional prayer request…" value={form.prayerRequest} onChange={e => setForm(f => ({ ...f, prayerRequest: e.target.value }))} rows={3} className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2.5 placeholder:text-silver-dim/40 focus:outline-none focus:border-gold transition-colors resize-none" />
              </div>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setForm(f => ({ ...f, firstTime: !f.firstTime }))} className={`w-5 h-5 border flex items-center justify-center transition-colors ${form.firstTime ? 'bg-gold border-gold' : 'bg-transparent border-border-strong'}`}>
                  {form.firstTime && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0A1628" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                </button>
                <span className="font-mono text-[11px] tracking-wider text-silver-dim uppercase">First-time visitor</span>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={handleSubmit} className="btn-gold text-[11px] px-6 py-3 font-mono tracking-widest flex-1">Save Visitor</button>
              <button onClick={() => setShowForm(false)} className="btn-ghost text-[11px] px-6 py-3 font-mono tracking-widest">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
